import { ReturnTrackingTriggerKey } from "@redotech/redo-flows-common/triggers";
import { KlaviyoEventType } from "@redotech/redo-integrations-definitions-common/klaviyo-integration";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const smsSignupForwarding = z.object({
  enabled: z.boolean(),
  listId: z.string().nullish(),
  customSource: z.string().nullish(),
});

export const updateKlaviyoIntegration = {
  name: "updateKlaviyoIntegration" as const,
  input: z.object({
    events: z.array(createEnum(KlaviyoEventType)).optional(),
    returnTrackingTriggers: z
      .array(createEnum(ReturnTrackingTriggerKey))
      .optional(),
    smsSignupForwarding: smsSignupForwarding.optional(),
  }),
  output: z.object({}),
};

export type UpdateKlaviyoIntegrationInput = z.infer<
  typeof updateKlaviyoIntegration.input
>;

export const setKlaviyoIntegration = {
  name: "setKlaviyoIntegration" as const,
  input: z.object({
    privateApiKey: z.string().min(1, "API key is required"),
    events: z.array(createEnum(KlaviyoEventType)).optional(),
    returnTrackingTriggers: z
      .array(createEnum(ReturnTrackingTriggerKey))
      .optional(),
    smsSignupForwarding: smsSignupForwarding.optional(),
  }),
  output: z.object({}),
};

export type SetKlaviyoIntegrationInput = z.infer<
  typeof setKlaviyoIntegration.input
>;

export const getKlaviyoIntegration = {
  name: "getKlaviyoIntegration" as const,
  input: z.object({}),
  output: z.object({
    klaviyoIntegration: z.optional(
      z.object({
        events: z.array(createEnum(KlaviyoEventType)),
        returnTrackingTriggers: z.array(createEnum(ReturnTrackingTriggerKey)),
        // Optional so a new client validating an old server's response during
        // the deploy window doesn't fail the whole settings page.
        smsSignupForwarding: smsSignupForwarding.optional(),
      }),
    ),
  }),
};

const getKlaviyoListsInput = z.object({
  // Klaviyo pages lists by opaque cursor; omit for the first chunk.
  cursor: z.string().optional(),
  // Ensures the currently saved list is in the first chunk even when it
  // sorts past it, so the picker can show the selection immediately.
  includeListId: z.string().optional(),
});

const getKlaviyoListsOutput = z.object({
  lists: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      optInProcess: z.string().nullish(),
    }),
  ),
  // Optional for deploy skew: null/absent once every list has been returned.
  nextCursor: z.string().nullish(),
  // Optional for deploy skew: false only when the key provably lacks
  // subscriptions:write, so forwarding would silently no-op.
  subscribeScopeOk: z.boolean().optional(),
  // Optional for deploy skew: false only when the key lacks profiles:write.
  // Non-blocking — forwarding still works, only new-profile names are lost.
  profileImportScopeOk: z.boolean().optional(),
});

export type GetKlaviyoListsInput = z.infer<typeof getKlaviyoListsInput>;
export type GetKlaviyoListsOutput = z.infer<typeof getKlaviyoListsOutput>;

export const getKlaviyoLists = {
  name: "getKlaviyoLists" as const,
  input: getKlaviyoListsInput,
  output: getKlaviyoListsOutput,
  mock: (_generated: GetKlaviyoListsOutput, input: GetKlaviyoListsInput) =>
    mockKlaviyoListsPage(input),
};

// Mirrors the real handler's paging: 100 lists per chunk (10 Klaviyo pages of
// 10), sorted by name, with the saved list pinned into the first chunk. Large
// enough to exercise scroll-to-load in mock mode without a backend.
const MOCK_KLAVIYO_LIST_COUNT = 355;
const MOCK_KLAVIYO_LISTS_PER_CHUNK = 100;
const MOCK_KLAVIYO_LIST_STEMS = [
  "Abandoned Cart",
  "Bracelet Waitlist",
  "Customer Care Outreach",
  "Disney Past Purchasers",
  "Email Sign Ups",
  "Newsletter",
  "SMS Subscribers",
  "VIP",
];

function mockKlaviyoList(index: number) {
  const stem =
    MOCK_KLAVIYO_LIST_STEMS[
      Math.floor(
        index / (MOCK_KLAVIYO_LIST_COUNT / MOCK_KLAVIYO_LIST_STEMS.length),
      )
    ] ?? "List";
  return {
    id: `mock-list-${String(index + 1).padStart(3, "0")}`,
    name: `${stem} ${String(index + 1).padStart(3, "0")}`,
    optInProcess: index % 3 === 0 ? "single_opt_in" : "double_opt_in",
  };
}

function mockKlaviyoListsPage(
  input: GetKlaviyoListsInput,
): GetKlaviyoListsOutput {
  const start = input.cursor ? Number(input.cursor) : 0;
  const end = Math.min(
    start + MOCK_KLAVIYO_LISTS_PER_CHUNK,
    MOCK_KLAVIYO_LIST_COUNT,
  );
  const lists = [];
  for (let index = start; index < end; index++) {
    lists.push(mockKlaviyoList(index));
  }
  if (
    input.includeListId &&
    !lists.some((list) => list.id === input.includeListId)
  ) {
    const pinnedIndex =
      Number(input.includeListId.replace("mock-list-", "")) - 1;
    if (
      Number.isInteger(pinnedIndex) &&
      pinnedIndex >= 0 &&
      pinnedIndex < MOCK_KLAVIYO_LIST_COUNT
    ) {
      lists.unshift(mockKlaviyoList(pinnedIndex));
    }
  }
  return {
    lists,
    nextCursor: end < MOCK_KLAVIYO_LIST_COUNT ? String(end) : null,
    subscribeScopeOk: true,
    profileImportScopeOk: true,
  };
}

export type GetKlaviyoIntegrationOutput = z.infer<
  typeof getKlaviyoIntegration.output
>;

export const disconnectKlaviyoIntegration = {
  name: "disconnectKlaviyoIntegration" as const,
  input: z.object({}),
  output: z.object({}),
};

export const sendTestKlaviyoEvents = {
  name: "sendTestKlaviyoEvents" as const,
  input: z.object({
    triggers: z.array(createEnum(ReturnTrackingTriggerKey)).min(1),
    email: z.email(),
  }),
  output: z.object({
    sent: z.number(),
    failures: z.array(
      z.object({
        trigger: createEnum(ReturnTrackingTriggerKey),
        error: z.string(),
      }),
    ),
  }),
};

export type SendTestKlaviyoEventsInput = z.infer<
  typeof sendTestKlaviyoEvents.input
>;
